/**
 * useAgentSession Hook - Clean React Implementation
 *
 * Provides global agent session management interface.
 * This hook gives access to the complete agent context with stable references.
 *
 * 🚀 FIXES:
 * - No more complex dependencies causing re-renders
 * - Stable function references
 * - Clean error handling
 * - Simple interface that just exposes the context
 */

import { useContext } from 'react';
import AgentSessionContext from '../context/AgentSessionContext';

/**
 * Hook for global agent session management
 *
 * This provides access to all agent functionality:
 * - Session lifecycle (create, load)
 * - Message sending (to current session)
 * - Query operations (get sessions, messages)
 * - State monitoring (agent state, connection status)
 *
 * @returns {AgentSessionContextValue} Complete agent session interface
 * @throws {Error} If used outside of AgentSessionProvider
 *
 * @example
 * ```javascript
 * // Welcome page - create new session and navigate
 * const agent = useAgentSession();
 *
 * const handleSubmit = async (message) => {
 *   const session = await agent.createSession(message);
 *   navigate(`/agent/chat/${session.id}`);
 * };
 *
 * // Get recent sessions for sidebar
 * const recentSessions = agent.getRecentSessions(10);
 * ```
 */
const useAgentSession = () => {
  const context = useContext(AgentSessionContext);

  if (!context) {
    throw new Error(
      'useAgentSession must be used within an AgentSessionProvider. ' +
      'Make sure your component is wrapped with <AgentSessionProvider>.'
    );
  }

  // ✅ Simple and clean - just return the context
  // The provider ensures all references are stable
  return context;
};

export default useAgentSession;